import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContactDetailsSectionBreadcrumbs,
  ContactDetailsSectionBreadcrumbsProps,
} from "../ContactDetailsSectionBreadcrumbs";
import { ContactDetailsSections } from "../../../ContactsDialog";
import { Activity, PencilRuler } from "lucide-react";
import { ObservationType } from "@tellescope/utils";

type VitalsSubsection = "add-bp-reading" | "add-other-reading" | "vitals-subtype" | null;

export interface VitalsSectionProps {
  enduser: TellescopeModelFromName<"endusers">;
}

export const VitalsSection = ({ enduser }: VitalsSectionProps) => {
  const [activeSubsection, setActiveSubsection] = useState<VitalsSubsection>();
  const [subtypeMetadata, setSubtypeMetadata] = useState<{
    title: string;
    observationId: string;
    observationType: ObservationType;
  }>();
  const { t } = useTranslation("vitals");

  const { breadcrumbs, actionButtons } = useMemo(() => {
    const breadcrumbs: ContactDetailsSectionBreadcrumbsProps["breadcrumbs"] = [
      {
        title: t("Vitals"),
        onClick: activeSubsection ? () => setActiveSubsection(null) : undefined,
      },
    ];
    const actionButtons: NonNullable<ContactDetailsSectionBreadcrumbsProps["actionButtons"]> = [];

    if (activeSubsection === "vitals-subtype") {
      breadcrumbs.push({
        title: subtypeMetadata?.title ?? t("Unknown"),
      });
    } else if (activeSubsection === "add-bp-reading") {
      breadcrumbs.push({
        title: t("Add BP Reading"),
      });
    } else if (activeSubsection === "add-other-reading") {
      breadcrumbs.push({
        title: t("Add Other Reading"),
      });
    } else if (!activeSubsection) {
      actionButtons.push({
        icon: Activity,
        onClick: () => setActiveSubsection("add-bp-reading"),
        hoverLabel: t("Add BP Reading"),
      });
      actionButtons.push({
        icon: PencilRuler,
        onClick: () => setActiveSubsection("add-other-reading"),
        hoverLabel: t("Add Other Reading"),
      });
    }

    return { breadcrumbs, actionButtons };
  }, [activeSubsection, subtypeMetadata, t]);

  return (
    <div className="flex flex-col gap-2 overflow-y-hidden">
      <ContactDetailsSectionBreadcrumbs
        enduserId={enduser.id}
        breadcrumbs={breadcrumbs}
        actionButtons={actionButtons}
      />
      {!activeSubsection && (
        <ContactDetailsSections.VitalsSection
          enduserId={enduser.id}
          onItemClick={(observationId, observationType, title) => {
            setActiveSubsection("vitals-subtype");
            setSubtypeMetadata({ title, observationId, observationType });
          }}
          onAddReading={(type) => {
            switch (type) {
              case "bp":
                setActiveSubsection("add-bp-reading");
                return;
              case "other":
                setActiveSubsection("add-other-reading");
                return;
            }
          }}
          hideButtons
        />
      )}
      {activeSubsection === "vitals-subtype" && subtypeMetadata?.observationType && (
        <ContactDetailsSections.VitalsProgramSubsection
          {...subtypeMetadata}
          enduserId={enduser.id}
          setObservationId={(id) => {
            setSubtypeMetadata((prev) =>
              prev?.title
                ? {
                    ...prev,
                    observationId: (typeof id === "function" ? id(prev.observationId) : id) ?? "",
                  }
                : prev
            );
          }}
          chartHeight="20svh"
        />
      )}
      {activeSubsection === "add-bp-reading" && (
        <ContactDetailsSections.AddBPReadingSubsection
          enduserId={enduser.id}
          onSubmit={() => setActiveSubsection(undefined)}
          onCancel={() => setActiveSubsection(undefined)}
        />
      )}
      {activeSubsection === "add-other-reading" && (
        <ContactDetailsSections.AddOtherReadingSubsection
          enduserId={enduser.id}
          onSubmit={() => setActiveSubsection(undefined)}
          onCancel={() => setActiveSubsection(undefined)}
        />
      )}
    </div>
  );
};
